<template>
  <div>
    <!-- Filtros -->
    <transition name="fade" mode="out-in">
      <b-card v-if="showFilters" no-body class="mb-2">
        <b-card-header class="d-flex justify-content-between align-items-center pl-1 pt-1">
          <div class="d-flex align-items-center">
            <h5 class="mb-0">{{ $t('Filters') }}</h5>
          </div>
          <b-avatar @click="showFilters = false" v-b-tooltip.hover :title="$t('Close Filters')" class="mr-1" button
            variant="danger" size="30px">
            <feather-icon icon="XIcon" />
          </b-avatar>
        </b-card-header>
        <b-card-body>
          <b-row>
            <!-- Filtro: Ativo -->
            <b-col cols="12" md="4" class="mb-2 mb-md-0">
              <h6 class="font-weight-bold mb-1">{{ $t('Asset') }}</h6>
              <vue-autosuggest v-model="searchAsset" :suggestions="filteredAssets" :input-props="inputAssetProps"
                @selected="selectAssetHandler" @input="onAssetInputChange">
                <template slot-scope="{suggestion}">
                  <span class="my-suggestion-item">{{ suggestion.item.name }}</span>
                </template>
              </vue-autosuggest>
            </b-col>

            <!-- Filtro: Intervalo de datas -->
            <b-col cols="12" md="4" class="mb-2 mb-md-0">
              <h6 class="font-weight-bold mb-1">{{ $t('Date Range') }} *</h6>
              <flat-pickr v-model="dateRange" class="form-control flatpickr-input" :config="dateRangeConfig"
                :placeholder="$t('Select date range')" />
              <small class="text-muted d-block mt-1">{{ $t('Maximum 31 days') }}</small>
            </b-col>

            <!-- Botão de busca -->
            <b-col cols="12" md="4" class="mb-2 mb-md-0">
              <h6 class="font-weight-bold mb-1">{{ $t('Action') }}</h6>
              <b-button variant="primary" block @click="loadData" class="search-btn"
                :disabled="loading || !dateRange || dateRange.length === 0">
                <feather-icon icon="SearchIcon" class="mr-1" />
                {{ $t('Search') }}
              </b-button>
            </b-col>
          </b-row>
          <b-row v-if="hasFilters" class="mt-1">
            <b-col cols="12" class="text-right">
              <b-button variant="outline-secondary" size="sm" @click="clearFilters">
                <feather-icon icon="XIcon" size="14" class="mr-50" />
                {{ $t('Clear filters') }}
              </b-button>
            </b-col>
          </b-row>
        </b-card-body>
      </b-card>
    </transition>

    <!-- Card do Mapa -->
    <b-card no-body :class="{ 'fullscreen-map': isFullscreen }">
      <b-card-header class="map-header">
        <!-- Primeira linha: Controles principais -->
        <div class="d-flex flex-wrap align-items-center mb-2 mb-md-0">
          <!-- Botão Filtros (quando oculto) -->
          <b-button v-if="!showFilters" variant="primary" size="sm" @click="showFilters = true" 
            class="mr-2 mb-2 mb-md-0 header-btn"
            v-b-tooltip.hover :title="$t('Open Filters')">
            <feather-icon icon="FilterIcon" />
            <span class="d-none d-sm-inline ml-1">{{ $t('Filters') }}</span>
          </b-button>

          <!-- Dropdown de Exportação -->
          <b-dropdown variant="outline-success" size="sm" no-caret
            :disabled="loading || exporting || !dateRange || dateRange.length === 0 || (occurrencesData.length === 0 && assetsData.length === 0)"
            class="mr-2 mb-2 mb-md-0 header-btn export-dropdown" left v-b-tooltip.hover :title="$t('Export data')">
            <template #button-content>
              <feather-icon v-if="!exporting" icon="DownloadIcon" />
              <b-spinner v-else small variant="success" />
              <span v-if="!exporting" class="ml-1 d-none d-lg-inline">{{ $t('Export') }}</span>
              <feather-icon v-if="!exporting" icon="ChevronDownIcon" size="14" class="ml-1" />
            </template>

            <b-dropdown-item @click="exportToExcel" :disabled="exporting" class="d-flex align-items-center">
              <feather-icon icon="FileTextIcon" size="16" class="mr-1 text-success" />
              <div class="d-flex flex-column">
                <span class="font-weight-bold">{{ $t('Export to Excel') }}</span>
                <small class="text-muted">{{ $t('Download as spreadsheet') }}</small>
              </div>
            </b-dropdown-item>

            <b-dropdown-item @click="exportToKML" :disabled="exporting" class="d-flex align-items-center">
              <feather-icon icon="NavigationIcon" size="16" class="mr-1 text-info" />
              <div class="d-flex flex-column">
                <span class="font-weight-bold">{{ $t('Export to KML') }}</span>
                <small class="text-muted">{{ $t('Download for Google Earth') }}</small>
              </div>
            </b-dropdown-item>
          </b-dropdown>

          <!-- Botão Fullscreen -->
          <b-button variant="outline-primary" size="sm" @click="toggleFullscreen" 
            class="mr-2 mb-2 mb-md-0 header-btn d-none d-md-inline-flex"
            v-b-tooltip.hover :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')">
            <feather-icon :icon="isFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'" />
          </b-button>
        </div>

        <!-- Segunda linha: Seletores (quebra em mobile) -->
        <div class="d-flex flex-wrap align-items-center map-controls">
          <!-- Seletor de conteúdo -->
          <b-form-radio-group v-model="displayContentMode" :options="displayContentOptions"
            button-variant="outline-success" size="sm" buttons 
            class="mr-2 mb-2 mb-md-0 map-control-group" />

          <!-- Seletor Heatmap/Pontos -->
          <b-form-radio-group v-model="occurrenceDisplayMode" :options="occurrenceDisplayOptions"
            button-variant="outline-primary" size="sm" buttons 
            class="mr-2 mb-2 mb-md-0 map-control-group"
            :disabled="displayContentMode === 'assets'" />

          <!-- Seletor de camada do mapa -->
          <b-form-radio-group v-model="selectedLayer" :options="layerOptions" 
            button-variant="outline-info" size="sm" buttons 
            class="mr-2 mb-2 mb-md-0 map-control-group" />

          <!-- Botão Fullscreen (mobile) -->
          <b-button variant="outline-primary" size="sm" @click="toggleFullscreen" 
            class="mb-2 mb-md-0 header-btn d-md-none"
            v-b-tooltip.hover :title="isFullscreen ? $t('Exit Fullscreen') : $t('Fullscreen')">
            <feather-icon :icon="isFullscreen ? 'MinimizeIcon' : 'MaximizeIcon'" />
          </b-button>
        </div>
      </b-card-header>

      <b-card-body class="p-0 position-relative">
        <!-- Overlay de Loading durante busca -->
        <div v-if="loading" class="loading-overlay">
          <div class="loading-content">
            <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
            <h5 class="mt-3 text-primary">{{ $t('Loading data') }}...</h5>
            <p class="text-muted mb-0">{{ $t('Please wait while we fetch the information') }}</p>
          </div>
        </div>

        <!-- Estado de Erro -->
        <div v-if="error && !loading" class="text-center py-5">
          <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger mb-2" />
          <h5 class="text-danger">{{ $t('Error loading data') }}</h5>
          <p class="text-muted">{{ errorMessage }}</p>
          <b-button variant="primary" @click="loadData">
            {{ $t('Try again') }}
          </b-button>
        </div>

        <!-- Estado Vazio -->
        <div v-if="!loading && !error && assetsData.length === 0 && occurrencesData.length === 0"
          class="text-center py-5">
          <feather-icon icon="MapPinIcon" size="48" class="text-muted mb-2" />
          <h5 class="text-muted">{{ $t('No data found') }}</h5>
          <p class="text-muted">{{ $t('Try adjusting your filters') }}</p>
        </div>

        <!-- Mapa -->
        <div v-if="!error && (assetsData.length > 0 || occurrencesData.length > 0)" id="safe-spot-map">
          <l-map ref="myMap" :center="center" :zoom="zoom" @ready="mapReady">
            <l-tile-layer :url="chooseUrl" />

            <!-- Marcadores de Ocorrências (Pontos) -->

            <template v-if="showOccurrencePoints">
              <l-circle-marker v-for="(occurrence, i) in occurrencesData" :key="`occurrence-${i}`"
                :lat-lng="[occurrence.lastLat, occurrence.lastLng]" :radius="5" :color="'#ffffff'" :weight="2"
                :fillColor="'#dc3545'" :fillOpacity="0.9" class="occurrence-marker">
                <l-popup>
                  <div class="occurrence-popup">
                    <div class="occurrence-popup-header">
                      <feather-icon icon="AlertCircleIcon" size="18" class="mr-1" />
                      <h6 class="font-weight-bold text-danger mb-0">
                        {{ $t('Occurrence') }}
                      </h6>
                    </div>
                    <hr class="my-2" />
                    <div class="occurrence-popup-body">
                      <div class="info-row">
                        <feather-icon icon="ClockIcon" size="14" class="mr-1 text-muted" />
                        <span class="text-muted">{{ $t('Date') }}:</span>
                        <strong class="ml-1">{{ occurrence.dateTime }}</strong>
                      </div>
                      <div class="info-row">
                        <feather-icon icon="TruckIcon" size="14" class="mr-1 text-muted" />
                        <span class="text-muted">{{ $t('Asset') }}:</span>
                        <strong class="ml-1">{{ occurrence.assetIdentifier }}</strong>
                      </div>
                      <div class="info-row" v-if="occurrence.tag">
                        <feather-icon icon="TagIcon" size="14" class="mr-1 text-muted" />
                        <span class="text-muted">{{ $t('Tag') }}:</span>
                        <strong class="ml-1">{{ occurrence.tag }}</strong>
                      </div>
                    </div>
                  </div>
                </l-popup>
              </l-circle-marker>
            </template>

            <!-- Marcadores de Ativos -->
            <l-marker v-for="(asset, i) in assetsData" :key="`asset-${i}`"
              v-if="showAssets && asset.lastLat && asset.lastLat !== 0 && asset.lastLng && asset.lastLng !== 0"
              :lat-lng="[asset.lastLat, asset.lastLng]" :z-index-offset="-200">
              <l-icon :class-name="`asset-marker asset-${asset.lastWorkstatus}`" :icon-size="[50, 50]"
                :icon-anchor="[25, 25]" :popup-anchor="[0, -25]">
                <div class="asset-icon-container">
                  <img :src="resolveAssetIcon(asset)" class="asset-icon-img" />
                </div>
              </l-icon>

              <l-popup>
                <div class="asset-popup">
                  <div class="text-center mb-2">
                    <img :src="resolveAssetIcon(asset)" class="popup-asset-img" />
                  </div>
                  <div class="popup-info">
                    <p class="mb-1">
                      <strong>{{ $t('Identifier') }}:</strong>
                      {{ asset.plate || asset.chassisNumber }}
                    </p>
                    <p class="mb-1" v-if="asset.nickName">
                      <strong>{{ $t('Nickname') }}:</strong>
                      {{ asset.nickName }}
                    </p>
                    <p class="mb-1">
                      <strong>{{ $t('Model') }}:</strong>
                      {{ asset.manufacturerName }} {{ asset.modelName }}
                    </p>
                    <p class="mb-1" v-if="asset.lastPositionTime">
                      <strong>{{ $t('Last Position') }}:</strong>
                      {{ formatDateTime(asset.lastPositionTime) }}
                    </p>
                    <p class="mb-0">
                      <strong class="mr-1">{{ $t('Status') }}:</strong>
                      <b-badge :variant="resolveStatusVariant(asset.lastWorkstatus)">
                        {{ $t(asset.lastWorkstatus) }}
                      </b-badge>
                    </p>
                  </div>
                </div>
              </l-popup>
            </l-marker>


          </l-map>
        </div>
      </b-card-body>

      <!-- Rodapé com estatísticas -->
      <b-card-footer v-if="!loading && !error && (assetsData.length > 0 || occurrencesData.length > 0)"
        class="stats-footer">
        <b-row class="align-items-center">
          <b-col cols="12" md="6" class="mb-3 mb-md-0">
            <div class="d-flex justify-content-center justify-content-md-start flex-wrap">
              <!-- Estatística de Ativos -->
              <div class="stat-card stat-card-primary mr-2 mb-2 mb-md-0">
                <div class="stat-icon">
                  <feather-icon icon="TruckIcon" size="24" />
                </div>
                <div class="stat-content">
                  <h3 class="stat-number mb-0">{{ assetsData.length }}</h3>
                  <span class="stat-label">{{ $t('Assets') }}</span>
                </div>
              </div>

              <!-- Estatística de Ocorrências -->
              <div class="stat-card stat-card-danger">
                <div class="stat-icon">
                  <feather-icon icon="AlertCircleIcon" size="24" />
                </div>
                <div class="stat-content">
                  <h3 class="stat-number mb-0">{{ occurrencesData.length }}</h3>
                  <span class="stat-label">{{ $t('Occurrences') }}</span>
                </div>
              </div>
            </div>
          </b-col>

          <!-- Período -->
          <b-col cols="12" md="6">
            <div class="period-display d-flex align-items-center justify-content-center justify-content-md-end">
              <feather-icon icon="CalendarIcon" size="18" class="text-muted mr-2 flex-shrink-0" />
              <div class="text-center text-md-left">
                <small class="text-muted d-block">{{ $t('Period') }}</small>
                <strong class="text-body d-block">{{ periodDisplay.start }} - {{ periodDisplay.end }}</strong>
              </div>
            </div>
          </b-col>
        </b-row>
      </b-card-footer>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BCardFooter,
  BRow,
  BCol,
  BButton,
  BFormRadioGroup,
  BSpinner,
  BBadge,
  BDropdown,
  BDropdownItem,
} from 'bootstrap-vue'

import {
  LMap,
  LTileLayer,
  LMarker,
  LIcon,
  LPopup,
  LCircleMarker,
} from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import L from 'leaflet'
import 'leaflet.heat'

import { VueAutosuggest } from 'vue-autosuggest'
import flatPickr from 'vue-flatpickr-component'
import 'flatpickr/dist/flatpickr.css'

import { ref, onUnmounted, onMounted } from '@vue/composition-api'
import store from '@/store'
import safeSpotStoreModule from './safeSpotStoreModule'
import assetStoreModule from '../asset/assetStoreModule'
import { getFlatpickrLocale } from '@/libs/i18n/flatpickrLocale'
import { getIntlLocale } from '@/libs/i18n/dateLocale'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BCardFooter,
    BRow,
    BCol,
    BButton,
    BFormRadioGroup,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    LMap,
    LTileLayer,
    LMarker,
    LIcon,
    LPopup,
    LCircleMarker,
    VueAutosuggest,
    flatPickr,
  },

  data() {
    return {
      // Filtros
      searchAsset: '',
      selectedAssetId: null,
      dateRange: [],

      // Sugestões
      filteredAssets: [],

      // Input props
      inputAssetProps: {
        id: 'autosuggest-asset',
        class: 'form-control',
        placeholder: this.$t('Search asset') + '...',
      },

      // Configuração do date picker (locale dinâmico via computed)
      dateRangeConfigBase: {
        mode: 'range',
        dateFormat: 'd/m/Y',
        maxRange: 31,
      },

      // Dados
      assetsData: [],
      occurrencesData: [],

      // Estado
      loading: false,
      error: false,
      errorMessage: '',
      exporting: false,

      // Mapa
      center: [-12.4131269, -55.5261856],
      zoom: 5,
      selectedLayer: 'map',
      occurrenceDisplayMode: 'points',
      displayContentMode: 'both',
      heatmapLayer: null,
      isFullscreen: false,
      showFilters: true,

      layerOptions: [
        { text: this.$t('Map'), value: 'map' },
        { text: this.$t('Satellite'), value: 'ground' },
      ],
      occurrenceDisplayOptions: [
        { text: this.$t('Points'), value: 'points' },
        { text: this.$t('Heatmap'), value: 'heatmap' },
      ],
      displayContentOptions: [
        { text: this.$t('Assets & Occurrences'), value: 'both' },
        { text: this.$t('Only Assets'), value: 'assets' },
        { text: this.$t('Only Occurrences'), value: 'occurrences' },
      ],
    }
  },

  computed: {
    dateRangeConfig() {
      const flatpickrLocale = getFlatpickrLocale()
      return {
        ...this.dateRangeConfigBase,
        ...(flatpickrLocale ? { locale: flatpickrLocale } : {}),
        onClose: (selectedDates) => {
          if (selectedDates.length === 2) {
            const diffTime = Math.abs(selectedDates[1] - selectedDates[0])
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

            if (diffDays > 31) {
              this.$swal(
                this.$t('Warning'),
                this.$t('The search period cannot exceed 31 days'),
                'warning',
              )
            }
          }
        },
      }
    },

    chooseUrl() {
      if (this.selectedLayer === 'map')
        return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
      if (this.selectedLayer === 'ground')
        return 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      return 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
    },

    hasFilters() {
      return this.searchAsset || this.selectedAssetId
    },

    showAssets() {
      return this.displayContentMode !== 'occurrences'
    },

    showOccurrencePoints() {
      return this.displayContentMode !== 'assets' && this.occurrenceDisplayMode === 'points'
    },

    showHeatmap() {
      return this.displayContentMode !== 'assets' && this.occurrenceDisplayMode === 'heatmap'
    },

    periodDisplay() {
      const empty = { start: '', end: '' }

      if (!this.dateRange) return empty

      if (Array.isArray(this.dateRange) && this.dateRange.length >= 2) {
        return {
          start: this.formatDate(this.dateRange[0]),
          end: this.formatDate(this.dateRange[1]),
        }
      }

      if (typeof this.dateRange === 'string' && this.dateRange.includes(' to ')) {
        const [start, end] = this.dateRange.split(' to ')
        return {
          start: this.formatDate(start),
          end: this.formatDate(end),
        }
      }

      return empty
    },
  },

  methods: {
    // Handlers de autocomplete - Asset
    async onAssetInputChange(text) {
      this.searchAsset = text || ''

      if (!text || text.length < 3) {
        this.selectedAssetId = null
        this.filteredAssets = []
        return
      }

      try {
        const response = await store.dispatch('app-asset/fetchAssets', {
          q: text,
          perPage: 10,
          page: 1,
        })

        this.filteredAssets = [
          {
            data: response.data.data.map(asset => ({
              id: asset.id,
              name: asset.plate || asset.chassisNumber || asset.nickName,
            })),
          },
        ]
      } catch (error) {
        this.filteredAssets = []
      }
    },

    selectAssetHandler(option) {
      this.selectedAssetId = option.item.id
      this.searchAsset = option.item.name
      this.filteredAssets = []
    },

    // Carregar dados
    async loadData() {
      if (!this.dateRange || this.dateRange.length === 0) {
        this.$swal(
          this.$t('Warning'),
          this.$t('Please select a date range'),
          'warning'
        )
        return
      }

      // Validar período máximo de 31 dias
      if (!this.validateDateRange()) {
        this.$swal(
          this.$t('Warning'),
          this.$t('The search period cannot exceed 31 days'),
          'warning'
        )
        return
      }

      this.loading = true
      this.error = false
      this.errorMessage = ''

      try {
        const [startDate, endDate] = this.parseDateRange(this.dateRange)
        const assetSearchTerm = this.searchAsset ? this.searchAsset.trim() : ''

        // Buscar ocorrências e ativos em paralelo
        const [occurrencesResponse, assetsResponse] = await Promise.all([
          // Buscar todas as ocorrências
          store.dispatch('app-safe-spot/fetchAllOccurrences', {
            assetId: this.selectedAssetId || undefined,
            startDate,
            endDate,
          }),
          // Buscar todos os ativos
          store.dispatch('app-asset/fetchAllAssetsForSafeSpot', {
            assetId: this.selectedAssetId || undefined,
            assetSearch: assetSearchTerm,
          })
        ])

        // Processar ocorrências (com verificação de segurança)
        const occurrencesData = occurrencesResponse?.data || []
        this.occurrencesData = occurrencesData.map(occurrence => ({
          lastLat: occurrence.lat,
          lastLng: occurrence.lng,
          dateTime: occurrence.dateTime,
          tag: occurrence.tag,
          assetIdentifier: occurrence.plate || occurrence.chassis || occurrence.assetId
        }))

        // Ativos já vêm filtrados (com verificação de segurança)
        this.assetsData = assetsResponse?.data || []

        // Ajustar o mapa para mostrar todos os pontos e atualizar heatmap
        this.$nextTick(() => {
          this.fitMapBounds()
          this.updateHeatmap()
          if (this.showOccurrencePoints) {
            this.bringOccurrencesToFront()
          }
        })
      } catch (error) {
        this.error = true
        this.errorMessage =
          error.response?.data?.message ||
          error.message ||
          this.$t('An error occurred while loading data')
      } finally {
        this.loading = false
      }
    },

    // Parsear intervalo de datas
    parseDateRange(range) {
      if (typeof range === 'string') {
        const dates = range.split(' to ')
        return dates.map(d => {
          const [day, month, year] = d.trim().split('/')
          return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
        })
      }
      return range
    },

    // Validar se o período não excede 31 dias
    validateDateRange() {
      if (!this.dateRange || typeof this.dateRange !== 'string') return false

      const dates = this.dateRange.split(' to ')
      if (dates.length !== 2) return false

      const [startDateStr, endDateStr] = dates

      // Converter para Date
      const [startDay, startMonth, startYear] = startDateStr.trim().split('/')
      const [endDay, endMonth, endYear] = endDateStr.trim().split('/')

      const startDate = new Date(startYear, startMonth - 1, startDay)
      const endDate = new Date(endYear, endMonth - 1, endDay)

      // Calcular diferença em dias
      const diffTime = Math.abs(endDate - startDate)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      return diffDays <= 31
    },

    // Ajustar bounds do mapa (incluindo ativos e ocorrências)
    fitMapBounds() {
      if (!this.$refs.myMap || !this.$refs.myMap.mapObject) return

      const bounds = []

      // Adicionar todas as ocorrências
      this.occurrencesData.forEach(occurrence => {
        if (occurrence.lastLat && occurrence.lastLng) {
          bounds.push([occurrence.lastLat, occurrence.lastLng])
        }
      })

      // Adicionar todos os ativos
      this.assetsData.forEach(asset => {
        if (asset.lastLat && asset.lastLat !== 0 && asset.lastLng && asset.lastLng !== 0) {
          bounds.push([asset.lastLat, asset.lastLng])
        }
      })

      if (bounds.length > 0) {
        this.$refs.myMap.mapObject.fitBounds(bounds, {
          padding: [50, 50],
          maxZoom: 15
        })
      }
    },

    // Resolver ícone do ativo
    resolveAssetIcon(asset) {
      if (asset.iconUrl && asset.iconUrl.trim()) {
        return asset.iconUrl
      }
      return require('@/assets/images/assets/Excavator.png')
    },

    // Resolver variante do status
    resolveStatusVariant(status) {
      const variants = {
        on: 'success',
        off: 'secondary',
        failure: 'danger',
        noconnection: 'light',
        severe: 'warning',
        iddle: 'info',
      }
      return variants[status] || 'light'
    },

    normalizeDateInput(date) {
      if (!date) return null

      if (date instanceof Date) return date

      if (typeof date === 'string') {
        const trimmed = date.trim()
        if (!trimmed) return null

        if (trimmed.includes('/')) {
          const parts = trimmed.split('/')
          if (parts.length === 3) {
            const [day, month, year] = parts.map(part => part.padStart(2, '0'))
            const parsed = new Date(Number(year), Number(month) - 1, Number(day))
            if (!Number.isNaN(parsed.getTime())) return parsed
          }
        }

        const parsed = new Date(trimmed)
        if (!Number.isNaN(parsed.getTime())) return parsed
      }

      return null
    },

    // Formatar data
    formatDate(date) {
      const parsedDate = this.normalizeDateInput(date)
      if (!parsedDate) return ''
      return parsedDate.toLocaleDateString(getIntlLocale())
    },

    // Formatar data e hora
    formatDateTime(date) {
      const parsedDate = this.normalizeDateInput(date)
      if (!parsedDate) return ''
      return `${parsedDate.toLocaleDateString(getIntlLocale())} ${parsedDate.toLocaleTimeString(getIntlLocale(), {
        hour: '2-digit',
        minute: '2-digit',
      })}`
    },

    // Inicializar período padrão (últimos 7 dias)
    initializeDefaultDateRange() {
      const today = new Date()
      const sevenDaysAgo = new Date()
      sevenDaysAgo.setDate(today.getDate() - 6)

      const formatDate = d => {
        const day = String(d.getDate()).padStart(2, '0')
        const month = String(d.getMonth() + 1).padStart(2, '0')
        const year = d.getFullYear()
        return `${day}/${month}/${year}`
      }

      this.dateRange = `${formatDate(sevenDaysAgo)} to ${formatDate(today)}`
    },

    mapReady() {
    },

    // Limpar filtros
    clearFilters() {
      this.searchAsset = ''
      this.selectedAssetId = null
      this.filteredAssets = []

      // Manter o date range e recarregar
      this.loadData()
    },

    // Toggle fullscreen
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen

      this.$nextTick(() => {
        if (this.$refs.myMap && this.$refs.myMap.mapObject) {
          this.$refs.myMap.mapObject.invalidateSize()
        }
      })
    },

    // Atualizar heatmap
    updateHeatmap() {
      if (!this.$refs.myMap || !this.$refs.myMap.mapObject) return

      // Remover heatmap existente
      if (this.heatmapLayer) {
        this.$refs.myMap.mapObject.removeLayer(this.heatmapLayer)
        this.heatmapLayer = null
      }

      // Se showHeatmap está ativo e há ocorrências, adicionar heatmap
      if (this.showHeatmap && this.occurrencesData.length > 0) {
        const heatPoints = this.occurrencesData
          .filter(occ => occ.lastLat && occ.lastLng)
          .map(occ => [occ.lastLat, occ.lastLng, 1]) // [lat, lng, intensity]

        if (heatPoints.length > 0) {
          this.heatmapLayer = L.heatLayer(heatPoints, {
            radius: 25,
            blur: 35,
            maxZoom: 17,
            max: 1.0,
            gradient: {
              0.0: '#0000ff',
              0.3: '#00ffff',
              0.5: '#00ff00',
              0.7: '#ffff00',
              1.0: '#ff0000'
            }
          })

          this.heatmapLayer.addTo(this.$refs.myMap.mapObject)
        }
      }

      if (this.showOccurrencePoints) {
        this.bringOccurrencesToFront()
      }
    },

    bringOccurrencesToFront() {
      if (!this.showOccurrencePoints) return
      if (!this.$refs.myMap || !this.$refs.myMap.mapObject) return

      this.$nextTick(() => {
        const map = this.$refs.myMap.mapObject
        map.eachLayer(layer => {
          if (layer instanceof L.CircleMarker) {
            layer.bringToFront()
          }
        })
      })
    },

    // Exportar para Excel
    async exportToExcel() {
      // Validar se há intervalo de datas
      if (!this.dateRange || this.dateRange.length === 0) {
        this.$swal(
          this.$t('Warning'),
          this.$t('Please select a date range'),
          'warning'
        )
        return
      }

      // Validar período máximo de 31 dias
      if (!this.validateDateRange()) {
        this.$swal(
          this.$t('Warning'),
          this.$t('The search period cannot exceed 31 days'),
          'warning'
        )
        return
      }

      // Validar se há dados para exportar
      if (this.occurrencesData.length === 0 && this.assetsData.length === 0) {
        this.$swal(
          this.$t('Warning'),
          this.$t('No data to export'),
          'warning'
        )
        return
      }

      this.exporting = true

      try {
        const [startDate, endDate] = this.parseDateRange(this.dateRange)

        // Chamar a action do store para exportar
        const response = await store.dispatch('app-safe-spot/exportOccurrencesToExcel', {
          assetId: this.selectedAssetId || null,
          startDate,
          endDate,
          page: 1,
          perPage: 100,
        })

        // Criar blob e fazer download
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url

        // Gerar nome do arquivo com data
        const formatDateForFile = (dateStr) => {
          if (typeof dateStr === 'string' && dateStr.includes('/')) {
            const [day, month, year] = dateStr.trim().split('/')
            return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`
          }
          return new Date().toISOString().split('T')[0].replace(/-/g, '')
        }

        const startDateFile = Array.isArray(this.dateRange)
          ? formatDateForFile(this.dateRange[0])
          : formatDateForFile(this.dateRange.split(' to ')[0])

        const endDateFile = Array.isArray(this.dateRange)
          ? formatDateForFile(this.dateRange[1] || this.dateRange[0])
          : formatDateForFile(this.dateRange.split(' to ')[1] || this.dateRange.split(' to ')[0])

        const fileName = `safe_spot_occurrences_${startDateFile}_${endDateFile}.xlsx`
        link.setAttribute('download', fileName)

        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        // Feedback de sucesso
        this.$toast.success(this.$t('Data exported successfully to Excel'))
      } catch (error) {
        this.$swal(
          this.$t('Error'),
          error.response?.data?.message ||
          error.message ||
          this.$t('An error occurred while exporting data'),
          'error'
        )
      } finally {
        this.exporting = false
      }
    },

    // Exportar para KML
    async exportToKML() {
      // Validar se há intervalo de datas
      if (!this.dateRange || this.dateRange.length === 0) {
        this.$swal(
          this.$t('Warning'),
          this.$t('Please select a date range'),
          'warning'
        )
        return
      }

      // Validar período máximo de 31 dias
      if (!this.validateDateRange()) {
        this.$swal(
          this.$t('Warning'),
          this.$t('The search period cannot exceed 31 days'),
          'warning'
        )
        return
      }

      // Validar se há dados para exportar
      if (this.occurrencesData.length === 0 && this.assetsData.length === 0) {
        this.$swal(
          this.$t('Warning'),
          this.$t('No data to export'),
          'warning'
        )
        return
      }

      this.exporting = true

      try {
        const [startDate, endDate] = this.parseDateRange(this.dateRange)

        // Chamar a action do store para exportar
        const response = await store.dispatch('app-safe-spot/exportOccurrencesToKML', {
          assetId: this.selectedAssetId || null,
          startDate,
          endDate,
          page: 1,
          perPage: 100,
        })

        // Criar blob e fazer download
        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url

        // Gerar nome do arquivo com data
        const formatDateForFile = (dateStr) => {
          if (typeof dateStr === 'string' && dateStr.includes('/')) {
            const [day, month, year] = dateStr.trim().split('/')
            return `${year}${month.padStart(2, '0')}${day.padStart(2, '0')}`
          }
          return new Date().toISOString().split('T')[0].replace(/-/g, '')
        }

        const startDateFile = Array.isArray(this.dateRange)
          ? formatDateForFile(this.dateRange[0])
          : formatDateForFile(this.dateRange.split(' to ')[0])

        const endDateFile = Array.isArray(this.dateRange)
          ? formatDateForFile(this.dateRange[1] || this.dateRange[0])
          : formatDateForFile(this.dateRange.split(' to ')[1] || this.dateRange.split(' to ')[0])

        const fileName = `safe_spot_occurrences_${startDateFile}_${endDateFile}.kml`
        link.setAttribute('download', fileName)

        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)

        // Feedback de sucesso
        this.$toast.success(this.$t('Data exported successfully to KML'))
      } catch (error) {
        this.$swal(
          this.$t('Error'),
          error.response?.data?.message ||
          error.message ||
          this.$t('An error occurred while exporting data'),
          'error'
        )
      } finally {
        this.exporting = false
      }
    },
  },

  watch: {
    occurrenceDisplayMode(newMode) {
      this.$nextTick(() => {
        this.updateHeatmap()
        if (newMode === 'points' && this.displayContentMode !== 'assets') {
          this.bringOccurrencesToFront()
        }
      })
    },

    displayContentMode() {
      this.$nextTick(() => {
        this.updateHeatmap()
      })
    },
  },

  mounted() {
    // Inicializar período padrão
    this.initializeDefaultDateRange()

    // Carregar dados iniciais
    this.loadData()
  },

  created() {
    const SAFE_SPOT_STORE_MODULE = 'app-safe-spot'
    const ASSET_STORE_MODULE = 'app-asset'

    if (!store.hasModule(SAFE_SPOT_STORE_MODULE)) {
      store.registerModule(SAFE_SPOT_STORE_MODULE, safeSpotStoreModule)
    }

    if (!store.hasModule(ASSET_STORE_MODULE)) {
      store.registerModule(ASSET_STORE_MODULE, assetStoreModule)
    }
  },

  destroyed() {
    const SAFE_SPOT_STORE_MODULE = 'app-safe-spot'
    const ASSET_STORE_MODULE = 'app-asset'

    if (store.hasModule(SAFE_SPOT_STORE_MODULE)) {
      store.unregisterModule(SAFE_SPOT_STORE_MODULE)
    }

    if (store.hasModule(ASSET_STORE_MODULE)) {
      store.unregisterModule(ASSET_STORE_MODULE)
    }
  },
}
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';

#safe-spot-map {
  .vue2leaflet-map.leaflet-container {
    height: 70vh !important;
    z-index: 0;
  }
}

// Loading overlay
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  min-height: 400px;

  .loading-content {
    text-align: center;
  }
}

// Fullscreen map
.fullscreen-map {
  position: fixed !important;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  margin: 0 !important;
  border-radius: 0 !important;
  max-width: 100% !important;

  #safe-spot-map {
    .vue2leaflet-map.leaflet-container {
      height: calc(100vh - 120px) !important;
    }
  }

  .card-body {
    min-height: calc(100vh - 120px);
  }

  .loading-overlay {
    min-height: calc(100vh - 120px);
  }
}

// Fade transition
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

// Marcadores de ativos
.asset-marker {
  padding: 3px;
  border-radius: 50%;
  border: 3px solid #fff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  width: auto !important;
  height: auto !important;
  display: flex;
  align-items: center;
  justify-content: center;
}

.asset-on {
  background-color: #28c76f !important;
}

.asset-off {
  background-color: #82868b !important;
}

.asset-failure {
  background-color: #ef5258 !important;
}

.asset-noconnection {
  background-color: #f5f4f8 !important;
}

.asset-severe {
  background-color: #ff9d54 !important;
}

.asset-iddle {
  background-color: #00cfe8 !important;
}

.asset-icon-container {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
}

.asset-icon-img {
  width: 32px;
  height: 32px;
  object-fit: contain;
}

// Popups
.asset-popup {
  min-width: 200px;
  max-width: 300px;

  .popup-asset-img {
    width: 60px;
    height: 60px;
    object-fit: contain;
  }

  .popup-info {
    p {
      font-size: 0.875rem;
      word-break: break-word;
    }
  }
  
  @media (max-width: 575px) {
    min-width: 180px;
    max-width: calc(100vw - 4rem);
    
    .popup-asset-img {
      width: 50px;
      height: 50px;
    }
    
    .popup-info {
      p {
        font-size: 0.8rem;
      }
    }
  }
}

.occurrence-popup {
  min-width: 260px;
  max-width: 320px;

  .occurrence-popup-header {
    display: flex;
    align-items: center;
  }

  .occurrence-popup-body {
    .info-row {
      display: flex;
      align-items: center;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;
      flex-wrap: wrap;
      
      span, strong {
        word-break: break-word;
      }
    }

    .occurrence-description {
      font-size: 0.875rem;
      margin-top: 0.25rem;
      margin-bottom: 0;
      padding: 0.5rem;
      background-color: #f8f9fa;
      border-radius: 0.25rem;
      border-left: 3px solid #dc3545;
      word-break: break-word;
    }
  }
  
  @media (max-width: 575px) {
    min-width: 200px;
    max-width: calc(100vw - 4rem);
    
    .occurrence-popup-body {
      .info-row {
        font-size: 0.8rem;
      }
      
      .occurrence-description {
        font-size: 0.8rem;
      }
    }
  }
}

// Círculos de ocorrências com sombra
.leaflet-interactive {
  &[fill='#dc3545'] {
    filter: drop-shadow(0 0 6px rgba(220, 53, 69, 0.5));
  }
}

// Efeito de transição suave no heatmap
.leaflet-heatmap-layer {
  transition: opacity 0.3s ease-in-out;
}

// Ajuste do flatpickr para ter mesmo background dos outros inputs
.flatpickr-input {
  background-color: #fff !important;
  width: 100%;

  &:disabled {
    background-color: #e9ecef !important;
  }
}

// Botão de busca - alinhado com os inputs
.search-btn {
  height: 38px;
  margin-top: 0;
  // Alinha com o input (ignorando o small text abaixo)
  align-self: flex-start;
  
  @media (max-width: 575px) {
    height: 42px;
    font-size: 0.9rem;
  }
}

// Garantir que os inputs tenham altura consistente
.flatpickr-input,
.vue-autosuggest input {
  height: 38px;
  
  @media (max-width: 575px) {
    height: 42px;
  }
}

// Dropdown de exportação
.export-dropdown {
  position: relative;
  
  .dropdown-toggle {
    &::after {
      display: none !important;
    }
  }
  
  .dropdown-menu {
    left: auto !important;
    right: 0 !important;
    margin-top: 0.5rem;
    z-index: 1051 !important; // Acima da sidebar que geralmente tem z-index 1050
    
    @media (max-width: 991px) {
      left: auto !important;
      right: 0 !important;
    }
  }
  
  // Garantir que o dropdown abra para a esquerda quando estiver perto da sidebar
  &.show {
    .dropdown-menu {
      left: auto !important;
      right: 0 !important;
      transform: translateX(0) !important;
    }
  }
}

// Estilos gerais para dropdowns
.dropdown-menu {
  min-width: 240px;
  padding: 0.5rem 0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: none;
  border-radius: 0.5rem;

  .dropdown-item {
    padding: 0.75rem 1rem;
    transition: all 0.2s ease;
    border: none;
    display: flex;
    align-items: center;

    &:hover {
      background-color: #f8f9fa;
      transform: translateX(2px);
    }

    &:active {
      background-color: #e9ecef;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }

    .feather {
      flex-shrink: 0;
      margin-right: 0.75rem;
    }

    >div {
      flex: 1;
      min-width: 0;

      span {
        display: block;
        line-height: 1.4;
      }

      small {
        display: block;
        margin-top: 0.125rem;
        font-size: 0.75rem;
        line-height: 1.3;
      }
    }
  }
}

// Estatísticas do rodapé
.stats-footer {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-top: 2px solid #e9ecef;
  padding: 1.5rem 1.25rem;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  border-radius: 0.5rem;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  background: white;
  min-width: 160px;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  }

  .stat-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin-right: 1rem;
    flex-shrink: 0;
  }

  .stat-content {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .stat-number {
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
  }

  .stat-label {
    font-size: 0.875rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-top: 0.25rem;
  }
}

.stat-card-primary {
  .stat-icon {
    background: linear-gradient(135deg, #7367f0 0%, #9e95f5 100%);
    color: white;
  }

  .stat-number {
    color: #7367f0;
  }

  .stat-label {
    color: #6c757d;
  }
}

.stat-card-danger {
  .stat-icon {
    background: linear-gradient(135deg, #ea5455 0%, #ff6b6b 100%);
    color: white;
  }

  .stat-number {
    color: #ea5455;
  }

  .stat-label {
    color: #6c757d;
  }
}

.period-display {
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: white;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  min-height: 68px;

  small {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  strong {
    font-size: 0.9rem;
  }
}

// Header do mapa - Responsividade
.map-header {
  padding: 1rem !important;
  
  @media (max-width: 767px) {
    padding: 0.75rem !important;
  }
}

.header-btn {
  flex-shrink: 0;
  white-space: nowrap;
}

.map-controls {
  @media (max-width: 575px) {
    width: 100%;
  }
}

.map-control-group {
  flex-shrink: 0;
  
  @media (max-width: 575px) {
    width: 100%;
    margin-right: 0 !important;
    margin-bottom: 0.5rem !important;
    
    .btn-group {
      width: 100%;
      display: flex;
      
      .btn {
        flex: 1;
      }
    }
  }
  
  @media (min-width: 576px) and (max-width: 991px) {
    .btn-group {
      .btn {
        font-size: 0.75rem;
        padding: 0.25rem 0.5rem;
      }
    }
  }
}

// Ajustes para os filtros em mobile
@media (max-width: 767px) {
  .card-header {
    .d-flex {
      flex-direction: column;
      align-items: flex-start !important;
    }
  }
  
  // Filtros
  .mb-2.mb-md-0 {
    margin-bottom: 0.5rem !important;
  }
}

// Responsividade para mobile
@media (max-width: 767px) {
  .stat-card {
    min-width: 140px;
    padding: 0.875rem 1rem;

    .stat-icon {
      width: 42px;
      height: 42px;
      margin-right: 0.75rem;
    }

    .stat-number {
      font-size: 1.5rem;
    }

    .stat-label {
      font-size: 0.75rem;
    }
  }

  .period-display {
    margin-top: 1rem;
  }
  
  // Ajustes no card de filtros
  .card-body {
    padding: 1rem 0.75rem !important;
  }
  
  // Ajustes nos botões de filtro
  .btn-sm {
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
  }
  
  // Ajustes no mapa
  #safe-spot-map {
    .vue2leaflet-map.leaflet-container {
      height: 50vh !important;
    }
  }
}

// Responsividade para tablets
@media (min-width: 768px) and (max-width: 991px) {
  .map-header {
    .map-control-group {
      .btn-group {
        .btn {
          font-size: 0.8rem;
          padding: 0.3rem 0.6rem;
        }
      }
    }
  }
  
  #safe-spot-map {
    .vue2leaflet-map.leaflet-container {
      height: 60vh !important;
    }
  }
}

// Ajustes para telas muito pequenas
@media (max-width: 575px) {
  .map-header {
    padding: 0.5rem !important;
  }
  
  // Ocultar tooltips em mobile muito pequeno
  [v-b-tooltip] {
    &::before,
    &::after {
      display: none !important;
    }
  }
  
  // Ajustes nos dropdowns
  .dropdown-menu {
    min-width: 200px;
    max-width: calc(100vw - 2rem);
  }
}
</style>
